import { DEFAULT_COMPOSED_CHART_Y_WIDTH_VERTICAL } from '@components/Widgets/utils';
import { format } from 'date-fns';
import { Margin } from 'recharts/types/util/types';
import { NO_VALUE } from '../../../views/CustomDashboards/constants';
import { AxisTickType } from '../../../views/CustomDashboards/types/types';
import { CUSTOM_CHART_LABEL_MAX_WIDTH } from '../CustomChartLabel';

export enum TickRotation {
  vertical = 'vertical',
  slanted = 'slanted',
  horizontal = 'horizontal',
}

export const barAxisTypeToRotation: Record<AxisTickType, TickRotation> = {
  [AxisTickType.Horizontal]: TickRotation.horizontal,
  [AxisTickType.Slanted]: TickRotation.slanted,
  [AxisTickType.Vertical]: TickRotation.vertical,
  [AxisTickType.Dynamic]: TickRotation.horizontal,
} as const;

export const getXTickMargin = (rotation: TickRotation, hasCustomFormatter = false) =>
  hasCustomFormatter ? (rotation === TickRotation.horizontal ? 24 : 16) : rotation === TickRotation.horizontal ? 8 : 0;

export const getXTickHeight = (rotation: TickRotation, hasLabel = false, hasCustomFormatter = false) => {
  // eslint-disable-next-line custom-rules/no-let
  let height = rotation === TickRotation.horizontal ? 46 : 100;
  if (!hasLabel) {
    height -= 16;
  }
  if (hasCustomFormatter) {
    height += 30;
  }
  return height;
};

export const getMinXTickSize = (rotation: TickRotation) =>
  ({
    [TickRotation.horizontal]: 30,
    [TickRotation.slanted]: 37,
    [TickRotation.vertical]: 14,
  })[rotation];

export const getMinYTickSize = (rotation: TickRotation) =>
  ({
    [TickRotation.horizontal]: 20,
    [TickRotation.slanted]: 20,
  })[rotation];

export const TICK_COUNT = 5;

export const getYTickWidth = (hasLabel = false, isVertical = false, customTickFormatter = false) => {
  if (!hasLabel) {
    return DEFAULT_COMPOSED_CHART_Y_WIDTH_VERTICAL;
  }

  if (isVertical && customTickFormatter) {
    return 136;
  }

  return 104;
};

export const getYAngle = (direction: AxisTickType = AxisTickType.Dynamic) => {
  if (direction === AxisTickType.Dynamic) {
    return TickRotation.horizontal;
  }

  return barAxisTypeToRotation[direction];
};

export const getXAngle = (direction: AxisTickType = AxisTickType.Dynamic, shouldRotateTicks?: boolean) => {
  if (direction === AxisTickType.Dynamic) {
    return shouldRotateTicks ? TickRotation.slanted : TickRotation.horizontal;
  }

  return barAxisTypeToRotation[direction];
};

export const formatValue = (isDate: boolean | undefined, dateFormat: string | undefined, str: string | null | undefined): string => {
  if (isDate && str) {
    return str === 'Others' ? str : format(new Date(str), dateFormat || 'MMM');
  }
  return str?.toString() || NO_VALUE;
};

export const getLabelPosition = (layout: 'vertical' | 'horizontal', stacked: boolean) => {
  if (layout === 'vertical' && stacked) {
    return { position: 'outsideBottomRight' };
  }

  if (layout === 'vertical' && !stacked) {
    return { position: 'right' };
  }

  if (layout === 'horizontal' && stacked) {
    return { position: 'outsideTopRight', offset: 4 };
  }

  return { position: 'top' };
};

export const getChartMargin = (showDataLabels: boolean, isHorizontal: boolean, isBar: boolean): Margin => {
  if (isBar) {
    return {
      bottom: 8,
      left: 8,
      right: showDataLabels && !isHorizontal ? CUSTOM_CHART_LABEL_MAX_WIDTH + 8 : 8,
      top: showDataLabels && isHorizontal ? 24 : 8,
    };
  }

  return {
    bottom: 10,
    left: 10,
    right: showDataLabels && !isHorizontal ? CUSTOM_CHART_LABEL_MAX_WIDTH + 8 : 10,
    top: showDataLabels && isHorizontal ? 24 : 10,
  };
};
